import { useEffect, useState } from 'react';
import { toZonedTime, format } from 'date-fns-tz';
import { startOfMonth, addMonths } from 'date-fns';

import { UNIPAY_APP_ID, DATE_TIME_FORMAT } from '@/shared/constants';
import { PlanType } from '@/types/plan';
import { GraduationType } from '@/types/graduation';
import { apiRequest } from '@/services';
import { PAYMENT_TYPE_TEXT } from '@/shared/constants/payment';

declare global {
  interface Window {
    UnivapayCheckout?: any;
    checkout?: any;
  }
}
let cancelController: AbortController | null = null;

const useUnivaPay = () => {
  const { apiService } = apiRequest();

  const [isReady, setIsReady] = useState<boolean>(!!window.UnivapayCheckout);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      !window.UnivapayCheckout &&
      !document.querySelector('#univapay-script')
    ) {
      const script = document.createElement('script');
      script.src = 'https://widget.univapay.com/client/checkout.js';
      script.id = 'univapay-script';
      script.async = true;
      script.onload = () => setIsReady(true);
      document.body.appendChild(script);
    } else if (
      typeof window !== 'undefined' &&
      window.UnivapayCheckout &&
      !isReady
    ) {
      setIsReady(true);
    }
  }, [isReady]);

  const handleCancelPayment = async (key: string) => {
    if (!key) return;

    if (cancelController) {
      cancelController.abort();
    }

    cancelController = new AbortController();

    await apiService.coach.collection('clear', {
      method: 'POST',
      body: { key },
      signal: cancelController.signal,
    });
  };

  const startCheckout = (
    paymentType: string,
    selectedPlan: PlanType,
    selectedGraduation: GraduationType,
    email: string,
    phoneNumber: string,
    key: string,
    onSuccessCallback: (subscriptionId: string) => void,
    subscriptionInitialAmount: string = '0',
  ) => {
    if (!window.UnivapayCheckout) {
      return;
    }

    const timeZone = 'Asia/Tokyo';
    const now = toZonedTime(new Date(), timeZone);
    const startDate = toZonedTime(
      selectedGraduation?.due_payment_date
        ? new Date(selectedGraduation.due_payment_date)
        : new Date(),
      timeZone,
    );

    let subscriptionStart: string;

    if (startDate <= now) {
      const nextMonthStart = startOfMonth(addMonths(now, 1));
      subscriptionStart = format(
        nextMonthStart,
        DATE_TIME_FORMAT.DATE_TIME_HYPHEN,
        { timeZone },
      );
    } else {
      const startOfSelected = startOfMonth(startDate);
      subscriptionStart = format(
        startOfSelected,
        DATE_TIME_FORMAT.DATE_TIME_HYPHEN,
        { timeZone },
      );
    }

    const config = {
      appId: UNIPAY_APP_ID,
      checkout: 'payment',
      amount: selectedPlan.price,
      requireName: true,
      requirePhoneNumber: true,
      size: 'large',
      className: 'btn_ips',
      autoSubmit: true,
      autoClose: true,
      text: 'NALS DEMO',
      title: 'アートシフトトレーニングアプリ',
      tokenType: 'subscription',
      subscriptionRetryInterval: 'P3D',
      metadata: {
        'univapay-product-names': `アートシフトアプリ${selectedGraduation.period_number}期`,
      },
      email,
      phoneNumber,
      cvvAuthorize: true,
      subscriptionInitialAmount: subscriptionInitialAmount,
      subscriptionStart,
      subscriptionPeriod: 'monthly',
      subscriptionAmount: selectedPlan.price,
      currency: 'jpy',
      submitButtonText: '決済',
      locale: 'ja',
      onSuccess: (res: any) => {
        onSuccessCallback(res.subscriptionId);
      },
    };

    if (paymentType === PAYMENT_TYPE_TEXT.REGISTER) {
      window.addEventListener('univapay:closed', () =>
        handleCancelPayment(key),
      );
      window.addEventListener('univapay:error', () => handleCancelPayment(key));
      window.addEventListener('beforeunload', () => handleCancelPayment(key));
      window.addEventListener('unload', () => handleCancelPayment(key));
    }

    window.checkout = window.UnivapayCheckout.create(config);
    window.checkout?.open();
  };

  return {
    isReady,
    startCheckout,
  };
};

export default useUnivaPay;
